import { get, isEmpty, isNil, keyBy, set, uniq } from "lodash";
import { EDGE_KINDS } from "utils/constants";
import { evaluateConditions } from "utils/validation-rules/conditions";
import { isRoutingEdge } from "./graph";
import { exitsOf, isInputStep, rulesOf } from "./next-steps";
import { isWaitingType } from "./step-tabs";

export const SIMULATION_STOPS = {
  SHOWN: "SHOWN",
  WAITING: "WAITING",
  PARKED: "PARKED",
  STUCK: "STUCK",
  END: "END",
  LOOP: "LOOP",
};

export const SIMULATION_CHOICES = {
  CONTINUE: "CONTINUE",
  EXIT: "EXIT",
};

const MAX_HOPS_WITHOUT_STOPPING = 100;

const STATUS_ACCESSOR = "status";

const leafConditions = (node) => {
  if (isNil(node)) return [];
  if (Array.isArray(node)) return node.flatMap(leafConditions);
  if (node.all) return leafConditions(node.all);
  if (node.any) return leafConditions(node.any);
  if (!isNil(node.not)) return leafConditions(node.not);
  return [node];
};

export const simulationFields = (edges = [], variables = []) => {
  const variableByAccessor = keyBy(variables, "accessor");
  const leaves = edges
    .filter(isRoutingEdge)
    .flatMap((edge) => leafConditions(get(edge, "data.conditions")));
  const accessors = uniq(
    leaves
      .flatMap((leaf) => [
        get(leaf, "leftOperand.accessor"),
        get(leaf, "rightOperand.accessor"),
      ])
      .filter((accessor) => !isEmpty(accessor) && accessor !== STATUS_ACCESSOR),
  );
  return accessors.map((accessor) => {
    const variable = variableByAccessor[accessor];
    const rightValues = leaves
      .filter((leaf) => get(leaf, "leftOperand.accessor") === accessor)
      .map((leaf) => get(leaf, "rightOperand.value"));
    return {
      accessor,
      label: get(variable, "label") || accessor,
      options: get(variable, "options") || null,
      boolean: rightValues.some((value) => typeof value === "boolean"),
      suggestions: uniq(
        rightValues
          .filter((value) => !isNil(value) && value !== "")
          .filter((value) => typeof value !== "boolean")
          .map(String),
      ),
    };
  });
};

const contextFrom = (values = {}) =>
  Object.entries(values).reduce(
    (context, [accessor, value]) =>
      isNil(value) || value === "" ? context : set(context, accessor, value),
    {},
  );

const hasOutgoing = (edges, node) =>
  edges.some((edge) => edge.source === node.id);

const firstMatchingRule = (node, edges, context) =>
  rulesOf(edges, node.id).find(
    (edge) =>
      get(edge, "data.kind") === EDGE_KINDS.ALWAYS ||
      evaluateConditions(get(edge, "data.conditions") || [], {
        ...context,
        status: get(node, "data.name"),
      }),
  );

const stopTypeOf = (node, nodes) => {
  if (isInputStep(node, nodes)) return SIMULATION_STOPS.SHOWN;
  if (isWaitingType(get(node, "data.nodeType")))
    return SIMULATION_STOPS.WAITING;
  return null;
};

export const simulateJourney = ({
  nodes = [],
  edges = [],
  startId = null,
  values = {},
  choices = [],
}) => {
  const byId = keyBy(nodes, "id");
  const context = contextFrom(values);
  const steps = [];
  const takenEdges = [];
  let current = byId[startId];
  let choiceIndex = 0;
  let hops = 0;

  const stop = (type) => ({
    steps,
    edges: takenEdges,
    stop: {
      type,
      stepId: get(current, "id") || null,
      exits: current ? exitsOf(edges, current.id) : [],
    },
  });

  const move = (edge) => {
    takenEdges.push(edge.id);
    current = byId[edge.target];
    if (current) steps.push(current.id);
  };

  const takeExitChoice = () => {
    const choice = choices[choiceIndex];
    if (get(choice, "type") !== SIMULATION_CHOICES.EXIT) return false;
    const exit = exitsOf(edges, current.id).find(
      (edge) => edge.id === choice.edgeId,
    );
    if (!exit) return false;
    choiceIndex += 1;
    hops = 0;
    move(exit);
    return true;
  };

  if (!current) return stop(SIMULATION_STOPS.END);
  steps.push(current.id);

  for (;;) {
    if (!current) return stop(SIMULATION_STOPS.END);
    const stopType = stopTypeOf(current, nodes);
    if (stopType) {
      if (takeExitChoice()) continue;
      if (get(choices[choiceIndex], "type") !== SIMULATION_CHOICES.CONTINUE)
        return stop(stopType);
      choiceIndex += 1;
      hops = 0;
    }
    const rule = firstMatchingRule(current, edges, context);
    if (!rule) {
      if (!hasOutgoing(edges, current)) return stop(SIMULATION_STOPS.END);
      if (isEmpty(exitsOf(edges, current.id)))
        return stop(SIMULATION_STOPS.STUCK);
      if (takeExitChoice()) continue;
      return stop(SIMULATION_STOPS.PARKED);
    }
    hops += 1;
    if (hops > MAX_HOPS_WITHOUT_STOPPING) return stop(SIMULATION_STOPS.LOOP);
    move(rule);
  }
};
